import { ResizeObserver } from '@juggle/resize-observer';
import { Range } from 'quill/core/selection';
import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { animated, useSpring } from 'react-spring';
import styled, { css, useTheme } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { zIndices } from '@hivebrite/design-system/Theme';
import { useOnClickOutside } from '@hivebrite/react-hooks';

import { useQuillEditorContext } from 'hb-react/shared/components/DesignSystem/QuillEditor/contexts/QuillEditorContext';

import Align from './Align';
import Bold from './Bold';
import Color from './Color';
import Font from './Font';
import Italic from './Italic';
import Link from './Link';
import List from './List';
import Size from './Size';
import Strikethrough from './Strikethrough';
import Underline from './Underline';

const TOOLBAR_ITEMS = {
  align: Align,
  bold: Bold,
  color: Color,
  font: Font,
  italic: Italic,
  link: Link,
  list: List,
  size: Size,
  strikethrough: Strikethrough,
  underline: Underline,
};

const ToolbarFloat = ({
  brandColors = null,
  containerElement = document.querySelector('body'),
  displayTriangle = false,
  positionMargin = 0,
  toolbarComponent: ToolbarComponent = WrapperToolbar,
  variant = null,
  withAnimation = false,
}) => {
  const theme = useTheme();
  const rowPadding = theme.space[2];

  const {
    isEditorFocus,
    offEditorChange,
    onEditorChange,
    options: { fonts, toolbarFloats },
    quillRef,
  } = useQuillEditorContext();

  const toolbarWrapperRef = useRef();
  const immediateAnimationRef = useRef(true);
  const prevImmediateAnimationRef = useRef(immediateAnimationRef.current);

  const [focusItem, setFocusItem] = useState();
  const [selectionBounds, setSelectionBounds] = useState(null);
  const [toolbarBounds, setToolbarBounds] = useState({});
  const [isToolbarEditing, setIsToolbarEditing] = useState(false);
  const [selectionFormat, setSelectionFormat] = useState(null);
  const [showTriangle, setShowTriangle] = useState(displayTriangle);
  const [toolbarWidth, setToolbarWidth] = useState(null);

  const [springToolbar, springToolbarApi] = useSpring(() => ({
    immediate: true,
    bounds: [0, 0],
  }));

  const handleClickItem = useCallback(
    (type) => {
      if (focusItem !== type) {
        setFocusItem(type);
      } else {
        setFocusItem(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleEditorChange = useCallback(
    () => {
      if (!quillRef.current) return;

      const selection = quillRef.current.getSelection();

      // Don't do anything if we don't have selection from QuillJS
      if (selection === null) {
        return;
      }

      const { index: cursorPos, length: cursorLength } = selection;

      const selectionFormat = quillRef.current.getFormat();

      // Only do something if we have minimum 1 character in the selection AND if the cursor is not on a link
      // OR on the Article page, only do something if the cursor is on a link
      if (
        (cursorLength === 0 && !selectionFormat.link) ||
        (variant === 'article' && !selectionFormat.link)
      ) {
        setSelectionBounds(null);

        return;
      }

      const lines = quillRef.current.getLines(cursorPos, cursorLength);

      let bounds = null;

      if (lines.length > 1) {
        const [firstLine] = lines;
        const index = quillRef.current.getIndex(firstLine);
        const length = Math.min(
          firstLine.length() - 1,
          cursorPos + cursorLength - index,
        );

        bounds = quillRef.current.getBounds(new Range(index, length));
      } else {
        bounds = quillRef.current.getBounds(new Range(cursorPos, cursorLength));
      }

      if (bounds) {
        setSelectionBounds(bounds);
      }

      setSelectionFormat(selectionFormat);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleToolbarEditing = useCallback(
    (value) => setIsToolbarEditing(value),
    [],
  );

  const handleHideToolbar = useCallback(
    () => (!isEditorFocus || isToolbarEditing) && setSelectionBounds(null),
    [isEditorFocus, isToolbarEditing],
  );

  const handleSetShowTriangle = useCallback(
    (value) => setShowTriangle(value),
    [],
  );

  useOnClickOutside(
    focusItem !== 'link' && toolbarWrapperRef,
    handleHideToolbar,
  );

  useEffect(() => {
    if (isToolbarEditing) {
      prevImmediateAnimationRef.current = immediateAnimationRef.current;
      immediateAnimationRef.current = true;
    }
  }, [isToolbarEditing]);

  useLayoutEffect(
    () => {
      const container = containerElement.getBoundingClientRect();

      const updateToolbarPosition = () => {
        if (!selectionBounds || !toolbarWrapperRef?.current) {
          prevImmediateAnimationRef.current = immediateAnimationRef.current;
          immediateAnimationRef.current = true;

          return;
        }

        const toolbarRect = toolbarWrapperRef.current.getBoundingClientRect();
        const parentRect = quillRef.current.container.getBoundingClientRect();

        setToolbarWidth((prevState) => {
          if (prevState !== null) {
            return prevState;
          }

          return toolbarRect.width;
        });

        const { left, bottom, width, top } = selectionBounds;

        const positionTop = top - toolbarRect.height - positionMargin;
        const positionBottom = bottom + positionMargin;
        const positionCenter = left + width / 2 - toolbarRect.width / 2;
        const positionFixRight =
          container.x + container.width - parentRect.x - toolbarRect.width;
        const positionFixLeft = container.x - parentRect.x;

        let x = positionCenter;
        let y = positionTop;

        // If the toolbar is outside from the top
        if (positionTop + parentRect.y < container.y) {
          y = positionBottom;
        }
        // If the Toolbar is outside from the bottom
        else if (positionTop + parentRect.y > container.y) {
          y = positionTop;
        }

        // If the toolbar is outside from the right
        if (
          parentRect.x + positionCenter + toolbarRect.width >
          container.width + container.x
        ) {
          x = positionFixRight;
        }
        // If the toolbar is outside from the left
        else if (parentRect.x + positionCenter < container.x) {
          x = positionFixLeft;
        }

        springToolbarApi.start({
          immediate:
            prevImmediateAnimationRef.current || immediateAnimationRef.current,
          bounds: [x, y],
        });
        setToolbarBounds({
          x,
          y,
        });

        prevImmediateAnimationRef.current = immediateAnimationRef.current;
        immediateAnimationRef.current = false;
      };

      updateToolbarPosition();

      const observer = new ResizeObserver(updateToolbarPosition);

      if (toolbarWrapperRef.current) {
        observer.observe(toolbarWrapperRef.current);
      }

      return () => {
        observer.disconnect();
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isToolbarEditing, positionMargin, selectionBounds],
  );

  // Set event for QuillJS
  useEffect(
    () => {
      if (onEditorChange) {
        onEditorChange(handleEditorChange);

        return () => offEditorChange(handleEditorChange);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onEditorChange, offEditorChange],
  );

  const hasTriangle = displayTriangle && showTriangle && !isToolbarEditing;

  const canDisplayToolbarItems = () =>
    toolbarFloats.some((toolbarFloat) =>
      toolbarFloat?.items.some(({ type }) => {
        const ToolbarItem = TOOLBAR_ITEMS[type];

        if (ToolbarItem) {
          const value = ToolbarItem.getFormat(
            selectionFormat,
            quillRef.current,
            fonts,
          );

          const canDisplay = ToolbarItem.canDisplay
            ? ToolbarItem.canDisplay(quillRef)
            : true;

          return value && canDisplay;
        }

        return false;
      }),
    );

  if (!canDisplayToolbarItems() || !selectionBounds) {
    return null;
  }

  return (
    <Wrapper
      displayTriangle={displayTriangle}
      ref={toolbarWrapperRef}
      style={{
        transform: withAnimation
          ? springToolbar.bounds.to((x, y) => `translate3d(${x}px, ${y}px, 0)`)
          : `translate3d(${toolbarBounds.x}px, ${toolbarBounds.y}px, 0)`,
      }}
    >
      <ToolbarComponent toolbarWidth={toolbarWidth}>
        {toolbarFloats.map(({ key, ...toolbarFloat }) => (
          <Fragment key={key}>
            <WrapperItems
              rowPadding={rowPadding}
              spaceBetweenItems={toolbarFloat?.spaceBetweenItems}
              width="100%"
            >
              {toolbarFloat?.items.map(({ textVariables, type, label }) => {
                const ToolbarItem = TOOLBAR_ITEMS[type];

                if (ToolbarItem) {
                  const value = ToolbarItem.getFormat(
                    selectionFormat,
                    quillRef.current,
                    fonts,
                  );

                  return (
                    <ToolbarItem
                      brandColors={brandColors}
                      fonts={fonts}
                      key={type}
                      label={label}
                      onItemClick={() => handleClickItem(type)}
                      onSetShowTriangle={handleSetShowTriangle}
                      onToolbarEditing={handleToolbarEditing}
                      value={value}
                      variant={variant}
                      textVariables={textVariables}
                      {...toolbarFloat?.itemProps?.({ value })}
                    />
                  );
                }

                return null;
              })}
            </WrapperItems>
            {hasTriangle && <Triangle />}
          </Fragment>
        ))}
      </ToolbarComponent>
    </Wrapper>
  );
};

export default ToolbarFloat;

const Wrapper = styled(animated.div).withConfig({
  shouldForwardProp: (prop) => !['displayTriangle', 'rows'].includes(prop),
})`
  font: unset !important;
  font-size: ${(props) => props.theme.fontSizes[1]} !important;
  text-align: left !important;
  left: 0;
  pointer-events: none;
  position: absolute;
  width: max-content;
  z-index: ${zIndices.popover};

  ${({ displayTriangle }) => css`
    top: ${displayTriangle ? '-10' : '0'}px;
  `}
`;

const WrapperToolbar = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) => !['toolbarWidth'].includes(prop),
  })
  .attrs(() => ({
    bg: 'white',
    px: 2,
  }))`
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.dove};
  position: relative;
  min-height: 38px;
  width: auto;

  > :not(:last-child) {
    padding-bottom: 0 !important;
  }
`;

const WrapperItems = styled(Box)
  .withConfig({
    shouldForwardProp: (prop) =>
      !['rowPadding', 'spaceBetweenItems'].includes(prop),
  })
  .attrs(() => ({
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'space-around',
  }))`
    pointer-events: all;
    ${({ spaceBetweenItems, rowPadding }) =>
      spaceBetweenItems &&
      css`
        &:not(:last-child) {
          padding: ${rowPadding}px ${rowPadding}px 0px ${rowPadding}px;
        }

        padding: ${rowPadding}px;
      `}
    ${({ spaceBetweenItems, theme }) =>
      spaceBetweenItems &&
      css`
        > *:not(:first-child) {
          margin-left: ${theme.space[2]}px;
        }
      `}
`;
const Triangle = styled.div`
  background: ${(props) => props.theme.colors.white};
  border-bottom: 1px solid ${(props) => props.theme.colors.dove};
  border-right: 1px solid ${(props) => props.theme.colors.dove};
  bottom: -6px; // height / 2 - 1px border
  height: 10px;
  left: 50%;
  margin-left: -5px;
  position: absolute;
  transform: rotate(45deg);
  width: 10px;
`;
